import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const networkSchema = z.object({
  eyebrow: z.string().max(80).optional(),
  title: z.string().min(1).max(160),
  titleHl: z.string().max(160).optional(),
  description: z.string().max(600).optional(),
  cities: z
    .array(
      z.object({
        name: z.string().min(1).max(80),
        isHq: z.boolean().default(false),
      }),
    )
    .min(2)
    .max(20),
  ctaLabel: z.string().max(80).optional(),
  ctaHref: z.string().max(500).optional(),
  bottomStats: z
    .array(
      z.object({
        value: z.string().min(1).max(40),
        label: z.string().min(1).max(120),
      }),
    )
    .max(4)
    .optional(),
});

export type NetworkData = z.infer<typeof networkSchema>;

export function NetworkRenderer({
  eyebrow,
  title,
  titleHl,
  description,
  cities,
  ctaLabel,
  ctaHref,
  bottomStats,
}: NetworkData) {
  return (
    <section className="v5-light v5-section v5-network">
      <div className="container">
        <div className="v5-network-grid">
          <div className="v5-network-text">
            {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
            <h2 className="v5-h2">
              {title}
              {titleHl ? (
                <>
                  <br />
                  <span className="v5-h2-hl">{titleHl}</span>
                </>
              ) : null}
            </h2>
            {description ? <p className="v5-section-sub">{description}</p> : null}
            <div className="v5-network-cities">
              {(cities ?? []).map((c, i) => (
                <span key={i} className="v5-city">
                  ● {c.name}
                  {c.isHq ? <> <em>HQ</em></> : null}
                </span>
              ))}
            </div>
            {ctaLabel && ctaHref ? (
              <a href={ctaHref} className="v5-btn-dark">
                {ctaLabel} →
              </a>
            ) : null}
          </div>
          <div className="v5-network-map">
            <svg viewBox="0 0 400 360" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M 60 90 Q 50 70 70 60 L 110 50 L 160 45 L 220 50 L 280 55 L 330 70 L 360 100 L 365 145 L 350 190 L 340 230 L 320 270 L 280 300 L 220 320 L 160 320 L 110 305 L 75 280 L 55 240 L 45 195 L 48 145 L 55 110 Z"
                fill="#e5e2db"
                stroke="#0a0b0f"
                strokeWidth="1.5"
                opacity="0.95"
              />
              <circle cx="380" cy="225" r="4" fill="#e5e2db" stroke="#0a0b0f" strokeWidth="1.5" />
              <circle cx="392" cy="218" r="3" fill="#e5e2db" stroke="#0a0b0f" strokeWidth="1.5" />
              <g stroke="#0a0b0f" strokeWidth="1" strokeDasharray="3 3" opacity="0.25">
                <line x1="195" y1="170" x2="280" y2="135" />
                <line x1="195" y1="170" x2="290" y2="195" />
                <line x1="195" y1="170" x2="180" y2="265" />
                <line x1="195" y1="170" x2="105" y2="125" />
                <line x1="195" y1="170" x2="125" y2="245" />
                <line x1="195" y1="170" x2="225" y2="100" />
                <line x1="195" y1="170" x2="80" y2="90" />
              </g>
              <g className="v5-map-dots">
                <circle cx="195" cy="170" r="14" fill="#d4ff00" opacity="0.3" />
                <circle cx="195" cy="170" r="9" fill="#d4ff00" />
                <circle cx="195" cy="170" r="4" fill="#0a0b0f" />
                <text x="208" y="174" fontFamily="Inter, sans-serif" fontSize="13" fontWeight="700" fill="#0a0b0f">Madrid HQ</text>
                <circle cx="290" cy="135" r="10" fill="#d4ff00" opacity="0.3" />
                <circle cx="290" cy="135" r="5" fill="#0a0b0f" />
                <text x="300" y="139" fontFamily="Inter, sans-serif" fontSize="11" fontWeight="600" fill="#0a0b0f">Barcelona</text>
                <circle cx="280" cy="195" r="10" fill="#d4ff00" opacity="0.3" />
                <circle cx="280" cy="195" r="5" fill="#0a0b0f" />
                <text x="290" y="199" fontFamily="Inter, sans-serif" fontSize="11" fontWeight="600" fill="#0a0b0f">Valencia</text>
                <circle cx="125" cy="245" r="10" fill="#d4ff00" opacity="0.3" />
                <circle cx="125" cy="245" r="5" fill="#0a0b0f" />
                <text x="60" y="249" fontFamily="Inter, sans-serif" fontSize="11" fontWeight="600" fill="#0a0b0f">Sevilla</text>
                <circle cx="180" cy="265" r="10" fill="#d4ff00" opacity="0.3" />
                <circle cx="180" cy="265" r="5" fill="#0a0b0f" />
                <text x="192" y="269" fontFamily="Inter, sans-serif" fontSize="11" fontWeight="600" fill="#0a0b0f">Málaga</text>
                <circle cx="225" cy="100" r="10" fill="#d4ff00" opacity="0.3" />
                <circle cx="225" cy="100" r="5" fill="#0a0b0f" />
                <text x="237" y="104" fontFamily="Inter, sans-serif" fontSize="11" fontWeight="600" fill="#0a0b0f">Bilbao</text>
                <circle cx="80" cy="90" r="10" fill="#d4ff00" opacity="0.3" />
                <circle cx="80" cy="90" r="5" fill="#0a0b0f" />
                <text x="20" y="94" fontFamily="Inter, sans-serif" fontSize="11" fontWeight="600" fill="#0a0b0f">A Coruña</text>
                <circle cx="245" cy="135" r="8" fill="#0a0b0f" opacity="0.3" />
                <circle cx="245" cy="135" r="4" fill="#0a0b0f" />
                <circle cx="380" cy="225" r="8" fill="#d4ff00" opacity="0.3" />
                <circle cx="380" cy="225" r="4" fill="#0a0b0f" />
                <text x="350" y="248" fontFamily="Inter, sans-serif" fontSize="10" fontWeight="600" fill="#0a0b0f">Baleares</text>
              </g>
            </svg>
            {bottomStats && bottomStats.length > 0 ? (
              <div className="v5-network-stats">
                {bottomStats.map((s, i) => (
                  <div key={i}>
                    <strong>{s.value}</strong>
                    {s.label}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

export const networkBlock: BlockDefinition<NetworkData> = {
  meta: {
    type: 'network',
    label: 'Red nacional / Oficinas (v5)',
    description: 'Mapa SVG estilizado de España con ciudades destacadas, lista de ciudades y stats.',
    category: 'content',
    icon: 'map',
  },
  schema: networkSchema,
  defaults: {
    eyebrow: '● Red nacional',
    title: 'Cerca de ti.',
    titleHl: 'Donde estés.',
    description: 'Operamos desde Madrid con red de profesionales y socios en las principales plazas españolas.',
    cities: [
      { name: 'Madrid', isHq: true },
      { name: 'Barcelona', isHq: false },
      { name: 'Valencia', isHq: false },
      { name: 'Sevilla', isHq: false },
      { name: 'Bilbao', isHq: false },
      { name: 'Málaga', isHq: false },
      { name: 'Zaragoza', isHq: false },
      { name: 'A Coruña', isHq: false },
    ],
    ctaLabel: 'Encuentra tu asesor',
    ctaHref: '/contacto',
    bottomStats: [
      { value: '8', label: 'plazas activas' },
      { value: '+24', label: 'profesionales' },
      { value: '17', label: 'provincias atendidas' },
    ],
  },
  fields: [
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 80 },
    { name: 'title', label: 'Título', type: 'text', required: true, max: 160 },
    { name: 'titleHl', label: 'Título highlight', type: 'text', max: 160 },
    { name: 'description', label: 'Descripción', type: 'textarea', max: 600 },
    {
      name: 'cities',
      label: 'Ciudades',
      type: 'array',
      max: 20,
      itemFields: [
        { name: 'name', label: 'Nombre', type: 'text', required: true, max: 80 },
        { name: 'isHq', label: '¿Es HQ?', type: 'boolean' },
      ],
    },
    { name: 'ctaLabel', label: 'Texto CTA', type: 'text', max: 80 },
    { name: 'ctaHref', label: 'URL CTA', type: 'url' },
    {
      name: 'bottomStats',
      label: 'Stats inferiores',
      type: 'array',
      max: 4,
      itemFields: [
        { name: 'value', label: 'Valor', type: 'text', required: true, max: 40 },
        { name: 'label', label: 'Etiqueta', type: 'text', required: true, max: 120 },
      ],
    },
  ],
  Renderer: NetworkRenderer,
};
